import { useEffect, useState, useCallback } from "react";
import { useParams, useOutletContext, useNavigate } from "react-router-dom";
import Topbar from "@/components/Topbar";
import { STATUS_META, apiClient, formatDate, API, TICKET_STATUS_LABELS, TICKET_STATUS_META, TICKET_PRIORITY_LABELS, TICKET_PRIORITY_META, TICKET_CATEGORY_LABELS } from "@/lib/api";
import { usePoles } from "@/context/PolesContext";
import StatusBadge from "@/components/StatusBadge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import AttachmentPicker from "@/components/AttachmentPicker";
import { MagnifyingGlass, FilePdf, CheckCircle, ArrowsClockwise, Archive, DownloadSimple, Eye, Plus, Image as ImageIcon, Paperclip, ArrowUUpLeft, CaretDown, CaretUp, Info, Ticket as TicketIcon, ArrowRight, SignIn, PencilSimple, Trash, WarningCircle, SortAscending, SortDescending } from "@phosphor-icons/react";
import { Checkbox } from "@/components/ui/checkbox";
import BulkConfirmDialog from "@/components/BulkConfirmDialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function PoleView() {
  const { poleLabel } = usePoles();
  const { poleId } = useParams();
  const navigate = useNavigate();
  const { refresh: refreshStats } = useOutletContext();
  const { user } = useAuth() || {};
  const isStaff = user && ["admin", "responsable", "employe"].includes(user.role);
  const [tab, setTab] = useState("inbox");
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [transactionSearch, setTransactionSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [chargeOpen, setChargeOpen] = useState(false);
  const [chargeTarget, setChargeTarget] = useState(null);
  const [chargeNumber, setChargeNumber] = useState("");

  // Tickets clients tab
  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [ticketsUnassigned, setTicketsUnassigned] = useState(0);

  // Internal request creation
  const [internalOpen, setInternalOpen] = useState(false);
  const [intObject, setIntObject] = useState("");
  const [intDescription, setIntDescription] = useState("");
  const [intFiles, setIntFiles] = useState([]);
  const [intSubmitting, setIntSubmitting] = useState(false);

  // Take-charge state
  const [chargeName, setChargeName] = useState("");

  // Details view
  const [detailsItem, setDetailsItem] = useState(null);
  // Edit request
  const [editRequest, setEditRequest] = useState(null);

  const poleName = poleLabel(poleId);

  const openInternal = () => {
    setIntObject("");
    setIntDescription("");
    setIntFiles([]);
    setInternalOpen(true);
  };

  const submitInternal = async (e) => {
    e.preventDefault();
    if (!intObject.trim()) {
      toast.error("L'objet est obligatoire");
      return;
    }
    setIntSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("sender_pole", poleId);
      fd.append("receiver_pole", poleId);
      fd.append("object", intObject.trim());
      fd.append("description", intDescription);
      intFiles.forEach((f) => fd.append("files", f));
      await apiClient.post("/requests", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Demande interne créée");
      setInternalOpen(false);
      fetchData();
      refreshStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec");
    } finally {
      setIntSubmitting(false);
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (tab === "inbox") {
        params.pole = poleId;
        params.direction = "received";
      } else if (tab === "sent") {
        params.pole = poleId;
        params.direction = "sent";
      } else if (tab === "archived") {
        params.pole = poleId;
        params.direction = "all";
        params.status = "archived";
      }
      if (debouncedSearch) params.q = debouncedSearch;
      if (transactionSearch) params.transaction_number = transactionSearch;
      const { data } = await apiClient.get("/requests", { params });
      // For inbox tab exclude archived (active workflow only)
      let list = data;
      if (tab === "inbox") list = list.filter((r) => r.status !== "archived");
      setItems(list);
    } catch (e) {
      console.error(e);
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, [poleId, tab, debouncedSearch, transactionSearch]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Load client tickets for this pole (when staff is logged in)
  const fetchTickets = useCallback(async () => {
    if (!isStaff) { setTickets([]); setTicketsUnassigned(0); return; }
    setTicketsLoading(true);
    try {
      const params = { service_id: poleId, limit: 200 };
      if (debouncedSearch) params.q = debouncedSearch;
      if (transactionSearch) params.transaction_number = transactionSearch;
      const { data } = await apiClient.get("/tickets", { params });
      setTickets(data);
      setTicketsUnassigned(
        data.filter((t) => t.status === "unassigned" || (t.status === "new" && !t.assignee_user_id)).length
      );
    } catch (e) {
      setTickets([]);
    } finally {
      setTicketsLoading(false);
    }
  }, [poleId, isStaff, debouncedSearch, transactionSearch]);

  useEffect(() => {
    fetchTickets();
    if (isStaff) {
      const id = setInterval(fetchTickets, 20000);
      return () => clearInterval(id);
    }
  }, [fetchTickets, isStaff]);

  const refreshAll = () => {
    fetchData();
    refreshStats();
  };

  const openCharge = (item) => {
    setChargeTarget(item);
    setChargeNumber(item.transaction_number || "");
    setChargeName("");
    setChargeOpen(true);
  };

  const submitCharge = async () => {
    if (!chargeName.trim()) {
      toast.error("Le nom de la personne en charge est obligatoire");
      return;
    }
    try {
      await apiClient.post(`/requests/${chargeTarget.id}/take-charge`, {
        taken_by_name: chargeName.trim(),
        transaction_number: chargeNumber.trim() || null,
      });
      toast.success("Demande prise en charge");
      setChargeOpen(false);
      refreshAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec");
    }
  };

  const completeRequest = async (item) => {
    try {
      await apiClient.post(`/requests/${item.id}/complete`);
      toast.success("Demande marquée comme traitée");
      refreshAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec");
    }
  };

  const revertToProcessing = async (item) => {
    try {
      await apiClient.post(`/requests/${item.id}/revert-to-processing`);
      toast.success("Retour au statut En cours");
      refreshAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec");
    }
  };

  const archiveRequest = async (item) => {
    try {
      await apiClient.post(`/requests/${item.id}/archive`);
      toast.success("Demande archivée");
      refreshAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec");
    }
  };

  const unarchiveRequest = async (item) => {
    try {
      await apiClient.post(`/requests/${item.id}/unarchive`);
      toast.success("Désarchivée — retour au statut Traité");
      refreshAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec");
    }
  };

  const viewFile = (item) => {
    window.open(`${API}/requests/${item.id}/file`, "_blank");
  };

  return (
    <>
      <Topbar
        title={`Pôle · ${poleName}`}
        subtitle="Boîte de réception, envois, archives et demandes internes"
        actions={
          <Button
            onClick={openInternal}
            variant="outline"
            className="rounded-sm h-9 px-3 sm:px-4 gap-2 border-zinc-300 hover:bg-zinc-100 text-xs sm:text-sm"
            data-testid={`pole-create-internal-${poleId}`}
          >
            <Plus size={14} weight="bold" />
            <span className="hidden sm:inline">Nouvelle demande interne</span>
            <span className="sm:hidden">Nouvelle</span>
          </Button>
        }
      />

      <main className="px-4 sm:px-6 lg:px-8 py-5 sm:py-8 max-w-[1600px] w-full" data-testid={`pole-view-${poleId}`}>
        <Tabs value={tab} onValueChange={setTab}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-4 sm:mb-5">
            <TabsList className="bg-white border border-zinc-200 rounded-sm p-1 w-full sm:w-auto">
              <TabsTrigger value="inbox" className="flex-1 sm:flex-none rounded-sm data-[state=active]:bg-zinc-900 data-[state=active]:text-white" data-testid="tab-inbox">Réception</TabsTrigger>
              <TabsTrigger value="sent" className="flex-1 sm:flex-none rounded-sm data-[state=active]:bg-zinc-900 data-[state=active]:text-white" data-testid="tab-sent">Envois</TabsTrigger>
              <TabsTrigger value="tickets" className="flex-1 sm:flex-none rounded-sm data-[state=active]:bg-zinc-900 data-[state=active]:text-white gap-1.5" data-testid="tab-tickets">
                <TicketIcon size={13} weight="bold" />
                <span>Tickets clients</span>
                {isStaff && ticketsUnassigned > 0 && (
                  <span className="ml-1 bg-red-600 text-white text-[9px] font-semibold px-1.5 py-0.5 rounded-sm min-w-[16px] text-center" data-testid="tab-tickets-red-badge">
                    {ticketsUnassigned}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="archived" className="flex-1 sm:flex-none rounded-sm data-[state=active]:bg-zinc-900 data-[state=active]:text-white" data-testid="tab-archived">Archives</TabsTrigger>
            </TabsList>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 flex-1 sm:max-w-xl">
              <div className="relative flex-1">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <Input
                  placeholder="N° de transaction..."
                  value={transactionSearch}
                  onChange={(e) => setTransactionSearch(e.target.value)}
                  className="h-10 pl-9 rounded-sm border-zinc-300 font-mono text-sm"
                  data-testid="search-transaction"
                />
              </div>
              <Input
                placeholder="Recherche libre (référence, client, e-mail, sujet…)"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-10 rounded-sm border-zinc-300"
                data-testid="search-text"
              />
            </div>
          </div>

          <TabsContent value="inbox">
            <RequestsTable
              items={items}
              loading={loading}
              poleId={poleId}
              tab={tab}
              onView={viewFile}
              onDetails={setDetailsItem}
              onTakeCharge={openCharge}
              onComplete={completeRequest}
              onRevert={revertToProcessing}
              onArchive={archiveRequest}
              onUnarchive={unarchiveRequest}
            />
          </TabsContent>
          <TabsContent value="sent">
            <RequestsTable items={items} loading={loading} poleId={poleId} tab={tab} onView={viewFile} onDetails={setDetailsItem} />
          </TabsContent>
          <TabsContent value="tickets">
            <TicketsPanel
              isStaff={isStaff}
              loading={ticketsLoading}
              tickets={tickets}
              onOpenTicket={(t) => navigate(`/tickets/${t.id}`)}
              onLogin={() => navigate("/login", { state: { from: `/pole/${poleId}` } })}
              onBulkDone={fetchTickets}
            />
          </TabsContent>
          <TabsContent value="archived">
            <RequestsTable
              items={items}
              loading={loading}
              poleId={poleId}
              tab={tab}
              onView={viewFile}
              onDetails={setDetailsItem}
              onUnarchive={unarchiveRequest}
            />
          </TabsContent>
        </Tabs>
      </main>

      {/* Take charge dialog */}
      <Dialog open={chargeOpen} onOpenChange={setChargeOpen}>
        <DialogContent className="rounded-sm" data-testid="take-charge-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Prendre en charge la demande</DialogTitle>
            <DialogDescription>
              Saisissez le nom de la personne qui prend en charge cette demande.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="bg-zinc-50 border border-zinc-200 px-3 py-2 text-sm">
              <div className="label-tiny text-zinc-500">Référence</div>
              <div className="font-medium font-mono">{chargeTarget?.object}</div>
            </div>
            <div className="space-y-2">
              <Label className="label-tiny text-zinc-500">Nom de la personne en charge *</Label>
              <Input
                value={chargeName}
                onChange={(e) => setChargeName(e.target.value)}
                placeholder="Ex: Jean Dupont"
                className="h-10 rounded-sm border-zinc-300"
                data-testid="taken-by-name-input"
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label className="label-tiny text-zinc-500">
                Numéro de transaction (optionnel)
                {chargeTarget?.transaction_number && (
                  <span className="ml-2 text-[10px] text-zinc-400 normal-case tracking-normal">(pré-rempli — modifiable)</span>
                )}
              </Label>
              <Input
                value={chargeNumber}
                onChange={(e) => setChargeNumber(e.target.value)}
                placeholder="Ex: TRX-2026-0001"
                className="h-10 rounded-sm border-zinc-300 font-mono"
                data-testid="transaction-number-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setChargeOpen(false)} className="rounded-sm" data-testid="cancel-charge-btn">Annuler</Button>
            <Button onClick={submitCharge} className="bg-zinc-900 hover:bg-zinc-800 rounded-sm" data-testid="confirm-charge-btn">
              Confirmer la prise en charge
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Internal request creation dialog */}
      <Dialog open={internalOpen} onOpenChange={setInternalOpen}>
        <DialogContent className="rounded-sm sm:max-w-lg" data-testid="internal-request-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Nouvelle demande interne · {poleName}</DialogTitle>
            <DialogDescription>
              Créer une demande à l'intérieur du pôle (note, tâche, document à traiter en interne). Le PDF est facultatif.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitInternal} className="space-y-4">
            <div className="space-y-2">
              <Label className="label-tiny text-zinc-500">Numéro de transaction *</Label>
              <Input
                value={intObject}
                onChange={(e) => setIntObject(e.target.value)}
                placeholder="Ex: TRX-2026-0001"
                className="h-10 rounded-sm border-zinc-300 font-mono"
                data-testid="internal-object-input"
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label className="label-tiny text-zinc-500">Description (optionnelle)</Label>
              <Textarea
                value={intDescription}
                onChange={(e) => setIntDescription(e.target.value)}
                placeholder="Détails, instructions, contexte..."
                className="rounded-sm border-zinc-300 min-h-[90px]"
                data-testid="internal-description-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="label-tiny text-zinc-500">Pièces jointes (optionnelles)</Label>
              <AttachmentPicker files={intFiles} setFiles={setIntFiles} compact testIdPrefix="internal" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setInternalOpen(false)} className="rounded-sm" data-testid="cancel-internal-btn">
                Annuler
              </Button>
              <Button type="submit" disabled={intSubmitting} className="bg-zinc-900 hover:bg-zinc-800 rounded-sm" data-testid="submit-internal-btn">
                {intSubmitting ? "Création..." : "Créer la demande"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Details dialog */}
      <Dialog open={!!detailsItem} onOpenChange={(o) => !o && setDetailsItem(null)}>
        <DialogContent className="rounded-sm sm:max-w-xl max-h-[90vh] overflow-y-auto" data-testid="details-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Détails de la demande</DialogTitle>
            <DialogDescription>Vue complète de la demande, accessible y compris pour les demandes archivées.</DialogDescription>
          </DialogHeader>
          {detailsItem && (
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <StatusBadge status={detailsItem.status} size="lg" />
                <span className="font-mono text-[11px] text-zinc-500">{formatDate(detailsItem.created_at)}</span>
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <DetailRow label="De" value={poleLabel(detailsItem.sender_pole)} />
                <DetailRow label="Vers" value={poleLabel(detailsItem.receiver_pole)} />
                <DetailRow label="Référence" value={detailsItem.object} mono />
                <DetailRow label="N° Transaction" value={detailsItem.transaction_number || "—"} mono />
                <DetailRow label="Pris en charge par" value={detailsItem.taken_by_name || "—"} />
                <DetailRow label="Pris en charge le" value={detailsItem.taken_at ? formatDate(detailsItem.taken_at) : "—"} />
                <DetailRow label="Traité le" value={detailsItem.completed_at ? formatDate(detailsItem.completed_at) : "—"} />
                <DetailRow label="Archivé le" value={detailsItem.archived_at ? formatDate(detailsItem.archived_at) : "—"} />
              </div>

              <div className="space-y-1.5">
                <div className="label-tiny text-zinc-500">Message / description</div>
                <div className="border border-zinc-200 bg-zinc-50 px-3 py-2.5 text-sm whitespace-pre-wrap break-words min-h-[60px]" data-testid="details-description">
                  {detailsItem.description ? detailsItem.description : <span className="text-zinc-400 italic">Aucun message</span>}
                </div>
              </div>

              {detailsItem.attachments && detailsItem.attachments.length > 0 && (
                <div className="space-y-1.5">
                  <div className="label-tiny text-zinc-500">Pièces jointes ({detailsItem.attachments.length})</div>
                  <div className="border border-zinc-200 divide-y divide-zinc-100">
                    {detailsItem.attachments.map((a) => (
                      <a
                        key={a.id}
                        href={`${API}/requests/${detailsItem.id}/attachments/${a.id}/file`}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-3 px-3 py-2 hover:bg-zinc-50"
                        data-testid={`details-att-${a.id}`}
                      >
                        {a.kind === "image" ? (
                          <ImageIcon size={20} weight="duotone" className="text-blue-600 shrink-0" />
                        ) : (
                          <FilePdf size={20} weight="duotone" className="text-red-600 shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{a.original_filename}</div>
                          <div className="text-[11px] text-zinc-500 font-mono">{a.kind.toUpperCase()} · {(a.size / 1024).toFixed(1)} Ko</div>
                        </div>
                        <DownloadSimple size={14} weight="bold" className="text-zinc-400" />
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="flex-row items-center gap-2 sm:justify-between">
            <div className="flex-1 text-[11px] text-zinc-500 min-w-0">
              {detailsItem?.updated_at && (
                <div className="truncate" data-testid="details-updated-note">
                  Modifiée le <span className="font-mono">{formatDate(detailsItem.updated_at)}</span>
                  {detailsItem.updated_by_name ? <> par <span className="font-medium">{detailsItem.updated_by_name}</span></> : null}
                </div>
              )}
            </div>
            <div className="flex gap-2">
              {isStaff && detailsItem && (
                <Button
                  variant="outline"
                  onClick={() => setEditRequest(detailsItem)}
                  className="rounded-sm gap-1.5"
                  data-testid="edit-request-btn"
                >
                  <PencilSimple size={14} weight="bold" /> Modifier
                </Button>
              )}
              {(user?.role === "admin" || user?.role === "responsable") && detailsItem && (
                <Button
                  variant="outline"
                  onClick={async () => {
                    if (!window.confirm(`Supprimer définitivement la demande « ${detailsItem.object} » ?\n\nLes pièces jointes seront également effacées. Cette action est irréversible.`)) return;
                    try {
                      await apiClient.delete(`/requests/${detailsItem.id}`);
                      toast.success("Demande supprimée");
                      setDetailsItem(null);
                      fetchData();
                      refreshStats && refreshStats();
                    } catch (e) {
                      toast.error(e.response?.data?.detail || "Échec");
                    }
                  }}
                  className="rounded-sm gap-1.5 border-red-300 text-red-600 hover:bg-red-50"
                  data-testid="delete-request-btn"
                >
                  <Trash size={14} weight="bold" /> Supprimer
                </Button>
              )}
              <Button variant="outline" onClick={() => setDetailsItem(null)} className="rounded-sm" data-testid="close-details-btn">
                Fermer
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit request dialog */}
      <EditRequestDialog
        target={editRequest}
        onOpenChange={(o) => !o && setEditRequest(null)}
        onSaved={(updated) => {
          setEditRequest(null);
          setDetailsItem(updated);
          fetchData();
          refreshStats && refreshStats();
        }}
      />
    </>
  );
}

function DetailRow({ label, value, mono }) {
  return (
    <div className="space-y-0.5">
      <div className="label-tiny text-zinc-500">{label}</div>
      <div className={`text-sm text-zinc-900 break-words ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}

function RequestsTable({ items, loading, tab, onView, onDetails, onTakeCharge, onComplete, onRevert, onArchive, onUnarchive }) {
  const { poleLabel } = usePoles();
  if (loading) {
    return (
      <div className="bg-white border border-zinc-200 px-6 py-12 text-center text-sm text-zinc-500" data-testid="loading-state">
        Chargement...
      </div>
    );
  }
  if (!items.length) {
    return (
      <div className="bg-white border border-zinc-200 px-6 py-16 text-center" data-testid="empty-state">
        <FilePdf size={32} weight="duotone" className="mx-auto text-zinc-300 mb-3" />
        <div className="text-sm text-zinc-500">Aucune demande à afficher</div>
      </div>
    );
  }

  return (
    <div data-testid="requests-table">
      {/* Mobile card view */}
      <div className="md:hidden space-y-2">
        {items.map((r) => (
          <RequestCard
            key={r.id}
            r={r}
            tab={tab}
            onView={onView}
            onDetails={onDetails}
            onTakeCharge={onTakeCharge}
            onComplete={onComplete}
            onRevert={onRevert}
            onArchive={onArchive}
            onUnarchive={onUnarchive}
          />
        ))}
      </div>

      {/* Desktop table view */}
      <div className="hidden md:block bg-white border border-zinc-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr>
              <Th>Date</Th>
              {tab === "inbox" || tab === "archived" ? <Th>De</Th> : <Th>Vers</Th>}
              <Th>Référence</Th>
              <Th>Statut</Th>
              <Th>N° Transaction</Th>
              <Th className="text-right">Actions</Th>
            </tr>
          </thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id} className="border-b border-zinc-100 hover:bg-zinc-50/50 transition-colors" data-testid={`request-row-${r.id}`}>
                <Td className="font-mono text-xs text-zinc-600 whitespace-nowrap">{formatDate(r.created_at)}</Td>
                <Td className="whitespace-nowrap">
                  <span className="px-2 py-0.5 bg-zinc-100 text-zinc-700 text-xs font-medium">
                    {tab === "sent" ? poleLabel(r.receiver_pole) : poleLabel(r.sender_pole)}
                  </span>
                </Td>
                <Td className="max-w-md">
                  <div className="flex items-center gap-1.5">
                    {r.attachments && r.attachments.length > 0 ? (
                      <span className="inline-flex items-center gap-1 text-[10px] font-mono shrink-0 px-1.5 py-0.5 bg-zinc-900 text-white">
                        <Paperclip size={10} weight="bold" />
                        {r.attachments.length}
                      </span>
                    ) : (
                      <span className="text-[10px] text-zinc-400 font-mono shrink-0 px-1.5 py-0.5 bg-zinc-100 border border-zinc-200">VIDE</span>
                    )}
                    <div className="font-medium text-zinc-900 break-words">{r.object}</div>
                  </div>
                  {r.description && <ExpandableText text={r.description} testId={`description-${r.id}`} />}
                  {r.attachments && r.attachments.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {r.attachments.map((a) => (
                        <a
                          key={a.id}
                          href={`${API}/requests/${r.id}/attachments/${a.id}/file`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-zinc-50 border border-zinc-200 hover:border-zinc-900 text-[11px] text-zinc-700"
                          title={a.original_filename}
                          data-testid={`att-${a.id}`}
                        >
                          {a.kind === "image" ? (
                            <ImageIcon size={11} weight="duotone" className="text-blue-600" />
                          ) : (
                            <FilePdf size={11} weight="duotone" className="text-red-600" />
                          )}
                          <span className="max-w-[120px] truncate">{a.original_filename}</span>
                        </a>
                      ))}
                    </div>
                  )}
                </Td>
                <Td>
                  <StatusBadge status={r.status} />
                  {r.taken_by_name && (
                    <div className="text-[10px] text-zinc-500 mt-1 flex items-center gap-1" data-testid={`taken-by-${r.id}`}>
                      <span className="label-tiny text-zinc-400">Par</span>
                      <span className="text-zinc-700 font-medium">{r.taken_by_name}</span>
                    </div>
                  )}
                </Td>
                <Td className="font-mono text-xs">
                  {r.transaction_number ? (
                    <span className="text-zinc-900 font-semibold" data-testid={`transaction-${r.id}`}>{r.transaction_number}</span>
                  ) : (
                    <span className="text-zinc-300">—</span>
                  )}
                </Td>
                <Td className="text-right whitespace-nowrap">
                  <div className="inline-flex items-center gap-1 justify-end">
                    <IconBtn
                      icon={<Info size={14} weight="bold" />}
                      title="Voir détails"
                      onClick={() => onDetails && onDetails(r)}
                      testId={`details-${r.id}`}
                    />
                    {r.has_file && (
                      <a
                        href={`${API}/requests/${r.id}/file`}
                        target="_blank"
                        rel="noreferrer"
                        className="p-2 hover:bg-zinc-100 transition-colors"
                        title="Voir / Télécharger"
                        data-testid={`download-${r.id}`}
                      >
                        <DownloadSimple size={14} weight="bold" />
                      </a>
                    )}
                    {tab === "inbox" && r.status === "unassigned" && (
                      <Button
                        size="sm"
                        onClick={() => onTakeCharge(r)}
                        className="bg-red-600 hover:bg-red-700 text-white rounded-sm h-7 px-2.5 text-xs gap-1"
                        data-testid={`take-charge-${r.id}`}
                      >
                        <ArrowsClockwise size={12} weight="bold" /> Prendre en charge
                      </Button>
                    )}
                    {tab === "inbox" && r.status === "processing" && (
                      <Button
                        size="sm"
                        onClick={() => onComplete(r)}
                        className="bg-green-600 hover:bg-green-700 text-white rounded-sm h-7 px-2.5 text-xs gap-1"
                        data-testid={`complete-${r.id}`}
                      >
                        <CheckCircle size={12} weight="bold" /> Marquer traité
                      </Button>
                    )}
                    {tab === "inbox" && r.status === "completed" && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onRevert && onRevert(r)}
                          className="rounded-sm h-7 px-2.5 text-xs gap-1 border-zinc-300 text-orange-700 hover:bg-orange-50"
                          data-testid={`revert-${r.id}`}
                          title="Annuler le statut traité et revenir à En cours"
                        >
                          <ArrowUUpLeft size={12} weight="bold" /> Annuler traité
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onArchive(r)}
                          className="rounded-sm h-7 px-2.5 text-xs gap-1 border-zinc-300"
                          data-testid={`archive-${r.id}`}
                        >
                          <Archive size={12} weight="bold" /> Archiver
                        </Button>
                      </>
                    )}
                    {tab === "archived" && r.status === "archived" && onUnarchive && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onUnarchive(r)}
                        className="rounded-sm h-7 px-2.5 text-xs gap-1 border-zinc-300 text-green-700 hover:bg-green-50"
                        data-testid={`unarchive-${r.id}`}
                        title="Désarchiver et revenir au statut Traité"
                      >
                        <ArrowUUpLeft size={12} weight="bold" /> Désarchiver
                      </Button>
                    )}
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </div>
    </div>
  );
}

function RequestCard({ r, tab, onView, onDetails, onTakeCharge, onComplete, onRevert, onArchive, onUnarchive }) {
  const { poleLabel } = usePoles();
  return (
    <div className="bg-white border border-zinc-200 p-3.5" data-testid={`request-card-${r.id}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            {r.attachments && r.attachments.length > 0 ? (
              <span className="inline-flex items-center gap-1 text-[10px] font-mono shrink-0 px-1.5 py-0.5 bg-zinc-900 text-white">
                <Paperclip size={10} weight="bold" /> {r.attachments.length}
              </span>
            ) : (
              <span className="text-[10px] text-zinc-400 font-mono shrink-0 px-1.5 py-0.5 bg-zinc-100 border border-zinc-200">VIDE</span>
            )}
            <span className="font-mono text-[10px] text-zinc-500">{formatDate(r.created_at)}</span>
          </div>
          <div className="font-medium text-zinc-900 mt-1.5 break-words">{r.object}</div>
        </div>
        <StatusBadge status={r.status} />
      </div>

      <div className="flex items-center gap-2 text-xs text-zinc-600 mb-2 flex-wrap">
        <span className="px-1.5 py-0.5 bg-zinc-100 text-zinc-700 text-[11px] font-medium">
          {tab === "sent" ? `→ ${poleLabel(r.receiver_pole)}` : `de ${poleLabel(r.sender_pole)}`}
        </span>
        {r.transaction_number && (
          <span className="font-mono text-[11px] text-zinc-700">#{r.transaction_number}</span>
        )}
        {r.taken_by_name && (
          <span className="text-[11px]"><span className="text-zinc-400">par</span> <span className="font-medium">{r.taken_by_name}</span></span>
        )}
      </div>

      {r.description && <ExpandableText text={r.description} testId={`description-mobile-${r.id}`} />}

      {r.attachments && r.attachments.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {r.attachments.map((a) => (
            <a
              key={a.id}
              href={`${API}/requests/${r.id}/attachments/${a.id}/file`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-zinc-50 border border-zinc-200 hover:border-zinc-900 text-[11px] text-zinc-700"
              data-testid={`mobile-att-${a.id}`}
            >
              {a.kind === "image" ? <ImageIcon size={11} weight="duotone" className="text-blue-600" /> : <FilePdf size={11} weight="duotone" className="text-red-600" />}
              <span className="max-w-[140px] truncate">{a.original_filename}</span>
            </a>
          ))}
        </div>
      )}

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-zinc-100">
        <button
          onClick={() => onDetails && onDetails(r)}
          className="inline-flex items-center gap-1.5 px-3 py-2 border border-zinc-300 hover:bg-zinc-100 text-xs font-medium"
          data-testid={`mobile-details-${r.id}`}
        >
          <Info size={14} weight="bold" /> Détails
        </button>
        {r.has_file && (
          <a
            href={`${API}/requests/${r.id}/file`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-2 border border-zinc-300 hover:bg-zinc-100 text-xs font-medium"
            data-testid={`mobile-download-${r.id}`}
          >
            <DownloadSimple size={14} weight="bold" /> Voir
          </a>
        )}
        {tab === "inbox" && r.status === "unassigned" && onTakeCharge && (
          <Button
            onClick={() => onTakeCharge(r)}
            className="bg-red-600 hover:bg-red-700 text-white rounded-sm h-9 px-3 text-xs gap-1 flex-1 min-w-[140px]"
            data-testid={`mobile-take-charge-${r.id}`}
          >
            <ArrowsClockwise size={14} weight="bold" /> Prendre en charge
          </Button>
        )}
        {tab === "inbox" && r.status === "processing" && onComplete && (
          <Button
            onClick={() => onComplete(r)}
            className="bg-green-600 hover:bg-green-700 text-white rounded-sm h-9 px-3 text-xs gap-1 flex-1 min-w-[140px]"
            data-testid={`mobile-complete-${r.id}`}
          >
            <CheckCircle size={14} weight="bold" /> Marquer traité
          </Button>
        )}
        {tab === "inbox" && r.status === "completed" && (
          <>
            {onRevert && (
              <Button
                variant="outline"
                onClick={() => onRevert(r)}
                className="rounded-sm h-9 px-3 text-xs gap-1 border-zinc-300 text-orange-700 hover:bg-orange-50"
                data-testid={`mobile-revert-${r.id}`}
              >
                <ArrowUUpLeft size={14} weight="bold" /> Annuler traité
              </Button>
            )}
            {onArchive && (
              <Button
                variant="outline"
                onClick={() => onArchive(r)}
                className="rounded-sm h-9 px-3 text-xs gap-1 border-zinc-300"
                data-testid={`mobile-archive-${r.id}`}
              >
                <Archive size={14} weight="bold" /> Archiver
              </Button>
            )}
          </>
        )}
        {tab === "archived" && r.status === "archived" && onUnarchive && (
          <Button
            variant="outline"
            onClick={() => onUnarchive(r)}
            className="rounded-sm h-9 px-3 text-xs gap-1 border-zinc-300 text-green-700 hover:bg-green-50"
            data-testid={`mobile-unarchive-${r.id}`}
          >
            <ArrowUUpLeft size={14} weight="bold" /> Désarchiver
          </Button>
        )}
      </div>
    </div>
  );
}

function Th({ children, className = "" }) {
  return (
    <th className={`text-left px-4 py-3 label-tiny text-zinc-500 ${className}`}>{children}</th>
  );
}
function Td({ children, className = "", ...rest }) {
  return <td className={`px-4 py-3 align-middle ${className}`} {...rest}>{children}</td>;
}
function IconBtn({ icon, title, onClick, testId }) {
  return (
    <button onClick={onClick} title={title} className="p-2 hover:bg-zinc-100 transition-colors" data-testid={testId}>
      {icon}
    </button>
  );
}

function ExpandableText({ text, testId }) {
  const [open, setOpen] = useState(false);
  const isLong = (text || "").length > 90;
  if (!isLong) {
    return <div className="text-xs text-zinc-500 mt-0.5 whitespace-pre-wrap break-words" data-testid={testId}>{text}</div>;
  }
  return (
    <div className="mt-0.5" data-testid={testId}>
      <div className={`text-xs text-zinc-500 whitespace-pre-wrap break-words ${open ? "" : "line-clamp-2"}`}>
        {text}
      </div>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="mt-1 text-[11px] text-zinc-700 hover:text-zinc-900 inline-flex items-center gap-1 font-medium"
        data-testid={`${testId}-toggle`}
      >
        {open ? <><CaretUp size={10} weight="bold" /> Réduire</> : <><CaretDown size={10} weight="bold" /> Voir plus</>}
      </button>
    </div>
  );
}

function TicketsPanel({ isStaff, loading, tickets, onOpenTicket, onLogin, onBulkDone }) {
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateDir, setDateDir] = useState("desc");
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [confirmAction, setConfirmAction] = useState(null);

  if (!isStaff) {
    return (
      <div className="bg-white border border-zinc-200 px-6 py-14 text-center" data-testid="tickets-panel-locked">
        <TicketIcon size={36} weight="duotone" className="mx-auto text-zinc-300 mb-3" />
        <div className="text-sm text-zinc-700 font-medium mb-1">Accès réservé aux collaborateurs</div>
        <div className="text-xs text-zinc-500 mb-4 max-w-sm mx-auto">
          Les tickets clients contiennent des données personnelles. Connectez-vous avec votre compte back-office pour les consulter.
        </div>
        <Button onClick={onLogin} className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-sm h-9 gap-2" data-testid="tickets-login-btn">
          <SignIn size={14} weight="bold" /> Se connecter
        </Button>
      </div>
    );
  }
  if (loading) {
    return (
      <div className="bg-white border border-zinc-200 px-6 py-12 text-center text-sm text-zinc-500" data-testid="tickets-loading">
        Chargement des tickets...
      </div>
    );
  }
  if (!tickets.length) {
    return (
      <div className="bg-white border border-zinc-200 px-6 py-14 text-center" data-testid="tickets-empty">
        <TicketIcon size={30} weight="duotone" className="mx-auto text-zinc-300 mb-3" />
        <div className="text-sm text-zinc-500">Aucun ticket client attribué à ce pôle pour l&apos;instant</div>
      </div>
    );
  }

  const activityOf = (t) => t.last_activity_at || t.created_at || "";
  const filtered = tickets
    .filter((t) => statusFilter === "all" || t.status === statusFilter)
    .filter((t) => !overdueOnly || t.is_overdue)
    .slice()
    .sort((a, b) => {
      const av = activityOf(a), bv = activityOf(b);
      if (av === bv) return 0;
      return dateDir === "asc" ? (av < bv ? -1 : 1) : (av > bv ? -1 : 1);
    });

  const toggleOne = (id) => setSelected((prev) => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const allSelected = filtered.length > 0 && filtered.every((t) => selected.has(t.id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(filtered.map((t) => t.id)));
  const runBulk = async (action) => {
    const ids = [...selected];
    if (!ids.length) return;
    try {
      const { data } = await apiClient.post("/tickets/bulk-action", { action, ticket_ids: ids });
      const verb = action === "close" ? "clôturé(s)" : "archivé(s)";
      toast.success(`${data.done} ticket(s) ${verb}${data.skipped ? ` · ${data.skipped} ignoré(s)` : ""}`);
      setSelected(new Set());
      onBulkDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de l'action groupée");
    } finally {
      setConfirmAction(null);
    }
  };

  return (
    <div data-testid="tickets-panel">
      {/* Filter bar */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-3">
        <div className="flex items-center gap-2">
          <span className="label-tiny text-zinc-500">Statut</span>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-9 w-[190px] rounded-sm border-zinc-300 text-xs" data-testid="pole-ticket-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les statuts</SelectItem>
              {Object.entries(TICKET_STATUS_LABELS).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <button
          onClick={() => setDateDir((d) => (d === "desc" ? "asc" : "desc"))}
          className="inline-flex items-center gap-1.5 h-9 px-3 border border-zinc-300 hover:bg-zinc-50 text-xs font-medium rounded-sm"
          data-testid="pole-ticket-date-sort"
          title="Trier par date de dernière activité"
        >
          {dateDir === "desc" ? <SortDescending size={15} weight="bold" /> : <SortAscending size={15} weight="bold" />}
          Dernière activité : {dateDir === "desc" ? "plus récent" : "plus ancien"}
        </button>
        <button
          onClick={() => setOverdueOnly((v) => !v)}
          className={`inline-flex items-center gap-1.5 h-9 px-3 border text-xs font-medium rounded-sm transition-colors ${overdueOnly ? "bg-red-600 text-white border-red-600" : "border-zinc-300 text-zinc-700 hover:bg-zinc-50"}`}
          data-testid="pole-ticket-overdue-filter"
          title="Afficher uniquement les tickets en retard (+72h)"
        >
          <WarningCircle size={15} weight={overdueOnly ? "fill" : "bold"} />
          En retard uniquement
        </button>
        <span className="sm:ml-auto font-mono text-xs text-zinc-500" data-testid="pole-tickets-count">{filtered.length} ticket{filtered.length > 1 ? "s" : ""}</span>
      </div>

      {selected.size > 0 && (
        <div className="mb-3 px-4 py-2.5 bg-zinc-900 text-white flex items-center justify-between gap-3 flex-wrap" data-testid="bulk-action-bar">
          <span className="text-xs font-medium">{selected.size} sélectionné{selected.size > 1 ? "s" : ""}</span>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={() => setConfirmAction("close")} className="rounded-sm h-8 text-xs bg-white text-zinc-900 hover:bg-zinc-200 gap-1.5" data-testid="bulk-close-btn">
              <CheckCircle size={14} weight="bold" /> Clôturer
            </Button>
            <Button size="sm" onClick={() => setConfirmAction("archive")} className="rounded-sm h-8 text-xs bg-white text-zinc-900 hover:bg-zinc-200 gap-1.5" data-testid="bulk-archive-btn">
              <Archive size={14} weight="bold" /> Archiver
            </Button>
            <button onClick={() => setSelected(new Set())} className="text-xs text-zinc-300 hover:text-white px-2" data-testid="bulk-clear-btn">Annuler</button>
          </div>
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="bg-white border border-zinc-200 px-6 py-12 text-center text-sm text-zinc-500" data-testid="pole-tickets-nomatch">
          Aucun ticket ne correspond à ce filtre.
        </div>
      ) : (
        <>
          {/* Mobile */}
          <div className="md:hidden space-y-2">
            {filtered.map((t) => (
              <div key={t.id} className={`flex items-start gap-3 bg-white border border-zinc-200 p-3.5 ${selected.has(t.id) ? "ring-1 ring-zinc-400" : ""}`}>
                <div className="pt-0.5" onClick={(e) => e.stopPropagation()}>
                  <Checkbox checked={selected.has(t.id)} onCheckedChange={() => toggleOne(t.id)} data-testid={`bulk-select-${t.id}`} aria-label="Sélectionner le ticket" />
                </div>
                <button
                  onClick={() => onOpenTicket(t)}
                  className="flex-1 text-left min-w-0"
                  data-testid={`ticket-card-${t.id}`}
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="font-mono text-xs font-semibold">{t.reference}</span>
                    <div className="flex items-center gap-1.5">
                      {t.is_overdue && <OverdueChip />}
                      <TicketStatusChip status={t.status} />
                    </div>
                  </div>
                  <div className="font-medium text-sm truncate">{t.subject}</div>
                  <div className="text-xs text-zinc-500 truncate mt-0.5">{t.client_first_name} {t.client_last_name} · {t.client_email}</div>
                  <div className="text-[11px] text-zinc-500 mt-1 flex items-center gap-2 flex-wrap">
                    <span className="px-1.5 py-0.5 bg-zinc-100">{TICKET_CATEGORY_LABELS[t.category] || t.category}</span>
                    <span className={TICKET_PRIORITY_META[t.priority]}>{TICKET_PRIORITY_LABELS[t.priority]}</span>
                    <span className="font-mono">{formatDate(activityOf(t))}</span>
                  </div>
                </button>
              </div>
            ))}
          </div>
          {/* Desktop */}
          <div className="hidden md:block bg-white border border-zinc-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 border-b border-zinc-200">
                  <tr>
                    <Th className="w-10"><Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="bulk-select-all" aria-label="Tout sélectionner" /></Th>
                    <Th>Référence</Th>
                    <Th>Client</Th>
                    <Th>Sujet</Th>
                    <Th>Catégorie</Th>
                    <Th>Statut</Th>
                    <Th>Priorité</Th>
                    <Th>N° Transaction</Th>
                    <Th>Dernière activité</Th>
                    <Th className="text-right">Ouvrir</Th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((t) => (
                    <tr
                      key={t.id}
                      onClick={() => onOpenTicket(t)}
                      className={`border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer ${selected.has(t.id) ? "bg-zinc-50" : ""}`}
                      data-testid={`ticket-row-${t.id}`}
                    >
                      <Td onClick={(e) => e.stopPropagation()}><Checkbox checked={selected.has(t.id)} onCheckedChange={() => toggleOne(t.id)} data-testid={`bulk-select-${t.id}`} aria-label="Sélectionner le ticket" /></Td>
                      <Td className="font-mono text-xs font-semibold whitespace-nowrap">{t.reference}</Td>
                      <Td>
                        <div className="font-medium text-sm">{t.client_first_name} {t.client_last_name}</div>
                        <div className="text-xs text-zinc-500 truncate max-w-[180px]">{t.client_email}</div>
                      </Td>
                      <Td className="max-w-xs">
                        <div className="font-medium truncate">{t.subject}</div>
                      </Td>
                      <Td className="text-xs">{TICKET_CATEGORY_LABELS[t.category] || t.category}</Td>
                      <Td>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <TicketStatusChip status={t.status} />
                          {t.is_overdue && <OverdueChip />}
                        </div>
                      </Td>
                      <Td className={`text-xs ${TICKET_PRIORITY_META[t.priority]}`}>{TICKET_PRIORITY_LABELS[t.priority]}</Td>
                      <Td className="font-mono text-xs">
                        {t.transaction_number ? <span className="text-zinc-900 font-semibold">{t.transaction_number}</span> : <span className="text-zinc-300">—</span>}
                      </Td>
                      <Td className="font-mono text-[11px] text-zinc-500 whitespace-nowrap">{formatDate(activityOf(t))}</Td>
                      <Td className="text-right"><ArrowRight size={14} weight="bold" className="text-zinc-400" /></Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
      <BulkConfirmDialog action={confirmAction} count={selected.size} onCancel={() => setConfirmAction(null)} onConfirm={() => runBulk(confirmAction)} />
    </div>
  );
}

function OverdueChip() {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold bg-red-600 text-white" data-testid="overdue-badge" title="Sans réponse depuis plus de 72h — remonté automatiquement">
      <WarningCircle size={11} weight="fill" /> En retard
    </span>
  );
}

function TicketStatusChip({ status }) {
  const m = TICKET_STATUS_META[status] || TICKET_STATUS_META.new;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] ${m.bg} ${m.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${m.dot}`} />
      {TICKET_STATUS_LABELS[status] || status}
    </span>
  );
}

function EditRequestDialog({ target, onOpenChange, onSaved }) {
  const [poles, setPoles] = useState([]);
  const [senderPole, setSenderPole] = useState("");
  const [receiverPole, setReceiverPole] = useState("");
  const [object, setObject] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!target) return;
    setSenderPole(target.sender_pole);
    setReceiverPole(target.receiver_pole);
    setObject(target.object || "");
    setDescription(target.description || "");
    apiClient.get("/poles").then(({ data }) => setPoles(data.filter((p) => p.is_active !== false)));
  }, [target]);

  if (!target) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!object.trim()) { toast.error("Le numéro de transaction est obligatoire"); return; }
    if (senderPole === receiverPole && senderPole !== target.sender_pole) {
      // internal request: sender==receiver is allowed (self-transfer marks it as pole-internal)
    }
    setSaving(true);
    try {
      const { data } = await apiClient.patch(`/requests/${target.id}`, {
        object: object.trim(),
        description: description.trim(),
        sender_pole: senderPole,
        receiver_pole: receiverPole,
      });
      toast.success("Demande modifiée");
      onSaved && onSaved(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!target} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-request-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Modifier la demande</DialogTitle>
          <DialogDescription>
            Créée le <span className="font-mono">{formatDate(target.created_at)}</span> · statut : <StatusBadge status={target.status} />
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="label-tiny text-zinc-500">Pôle expéditeur</Label>
              <select
                value={senderPole}
                onChange={(e) => setSenderPole(e.target.value)}
                className="h-10 w-full rounded-sm border border-zinc-300 bg-white px-3 text-sm"
                data-testid="edit-req-sender"
              >
                {poles.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="label-tiny text-zinc-500">Pôle destinataire</Label>
              <select
                value={receiverPole}
                onChange={(e) => setReceiverPole(e.target.value)}
                className="h-10 w-full rounded-sm border border-zinc-300 bg-white px-3 text-sm"
                data-testid="edit-req-receiver"
              >
                {poles.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="label-tiny text-zinc-500">Numéro de transaction / objet *</Label>
            <Input
              value={object}
              onChange={(e) => setObject(e.target.value)}
              className="h-10 rounded-sm border-zinc-300 font-mono"
              required
              data-testid="edit-req-object"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="label-tiny text-zinc-500">Description (ajoutez ici les infos complémentaires)</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="rounded-sm border-zinc-300 min-h-[100px]"
              placeholder="Précisions, notes internes, informations à ajouter..."
              data-testid="edit-req-description"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Annuler</Button>
            <Button type="submit" disabled={saving} className="bg-zinc-900 hover:bg-zinc-800 rounded-sm" data-testid="edit-req-submit">
              {saving ? "Enregistrement..." : "Enregistrer"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

